import { Button } from '@/components/UI/Button'
import { Typography } from '@/components/UI/Typography'
import type { ProductListItem } from '@/schemas/v2/ec'
import { Card } from '@mantine/core'
import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  product: ProductListItem
  /** 選択（購入開始）時のコールバック */
  onSelect: (product: ProductListItem) => void
  /** チェックアウト起動中フラグ（多重クリック防止） */
  submitting?: boolean
}

/** チケット（商品）1件を表示するカード。 */
export const TicketCard: FC<Props> = ({ product, onSelect, submitting = false }) => {
  const { t } = useTranslation()
  // 詳細説明の全文表示トグル（未展開時は3行で省略）
  const [descExpanded, setDescExpanded] = useState(false)

  // 購入可否はサーバが購入条件・在庫・購入済みを評価した purchasable を一次情報とする（#3383）。
  // 在庫判定を併用するのは purchasable を返さない旧APIでも売り切れを押させないため。
  const isSoldOut = product.stock !== null && product.stock <= 0
  const disabled = !product.purchasable || isSoldOut || submitting

  // ラベルは非活性の理由に合わせる（購入済み > 売り切れ > その他の購入不可）。
  // MERCHANDISE のように購入済みでも再購入できる商品は purchasable が true のままなので「購入する」を維持する。
  const resolveButtonLabel = (): string => {
    if (product.purchasable) {
      // purchasable を返さない旧API向けのフォールバック（在庫 0 は購入不可）
      return isSoldOut ? t('ec.ticket_select.sold_out', '売り切れ') : t('ec.ticket_select.buy', '購入する')
    }
    if (product.purchased) return t('ec.ticket_select.purchased', '購入済み')
    if (isSoldOut) return t('ec.ticket_select.sold_out', '売り切れ')
    return t('ec.ticket_select.unavailable', '購入できません')
  }
  const buttonLabel = resolveButtonLabel()

  return (
    <Card shadow="sm" padding={16} radius="sm" withBorder className="flex flex-col gap-2" style={{ paddingBottom: 24 }}>
      {/* 画像（一番上・カード幅いっぱい） */}
      <Card.Section>
        {product.imageUrl && <img src={product.imageUrl} alt={product.name} className="w-full object-cover" />}
      </Card.Section>

      {/* タイトル */}
      <Typography variant="subtitle1" component="h2" className="mt-2">
        {product.name}
      </Typography>

      {/* 詳細説明（未展開時は3行で省略。タップで全文表示） */}
      {product.description && (
        <button type="button" onClick={() => setDescExpanded((v) => !v)} className="w-full text-left">
          {/* line-clamp を確実に効かせるため body2 相当のスタイルを直接指定（SpotOverview と同様のプレーン要素） */}
          <span
            className={`text-[13px] leading-[20px] tracking-[-0.01em] text-gray-500 ${descExpanded ? 'block' : 'line-clamp-3'}`}
          >
            {product.description}
          </span>
        </button>
      )}

      {/* 一番下: 左=金額（primaryカラー）・右=購入ボタン */}
      <div className="mt-auto flex items-center justify-between gap-3 pt-2">
        <p className="flex items-baseline gap-1">
          <Typography variant="h3" component="span" className="text-black" style={{ fontWeight: 700 }}>
            {t('ec.ticket_select.price', '¥{{price}}', { price: product.salesAmount.toLocaleString() })}
          </Typography>
          <Typography variant="caption1" component="span" className="text-gray-500">
            {t('ec.ticket_select.tax_included', '税込')}
          </Typography>
        </p>
        <div className="shrink-0">
          <Button
            label={buttonLabel}
            variant="primary"
            size="sm"
            disabled={disabled}
            loading={submitting}
            onClick={() => onSelect(product)}
          />
        </div>
      </div>
    </Card>
  )
}
